import React, { useEffect, useState } from "react";
import { Dialog, DialogFooter } from "../../components/ui/Dialog";
import { Button } from "../../components/ui/Button";
import {
  Category,
  getCategories,
  updateCategory,
  deleteCategory,
} from "../../services/caregory";

interface CategoryDetailsDialogProps {
  category: Category | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdated?: () => void;
}

const CategoryDetailsDialog: React.FC<CategoryDetailsDialogProps> = ({
  category,
  isOpen,
  onClose,
  onUpdated,
}) => {
  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [subCategories, setSubCategories] = useState<string[]>([]);
  const [newSubCategory, setNewSubCategory] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  // Populate local state when category or dialog opens
  useEffect(() => {
    if (!category || !isOpen) return;
    setCode(category.code);
    setName(category.name);
    setSubCategories(category.subCategory || []);
  }, [category, isOpen]);

  const handleAddSubCategory = () => {
    const trimmed = newSubCategory.trim();
    if (!trimmed || subCategories.includes(trimmed)) return;
    setSubCategories([...subCategories, trimmed]);
    setNewSubCategory("");
  };

  const handleRemoveSubCategory = (sub: string) => {
    setSubCategories(subCategories.filter((s) => s !== sub));
  };

  const handleSave = async () => {
    if (!category) return;
    setSaving(true);
    try {
      await updateCategory(category.code, { code, name, subCategory: subCategories });
      onUpdated?.();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!category) return;
    setDeleting(true);
    try {
      await deleteCategory(category.code);
      onUpdated?.();
      onClose();
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Category Details"
      size="md"
      footer={
        category && (
          <DialogFooter className="justify-between">
            {!confirmDelete ? (
              <button
                onClick={() => setConfirmDelete(true)}
                className="px-4 py-1 text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
              >
                Delete
              </button>
            ) : (
              <div className="flex gap-2">
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-4 py-1 text-white bg-red-600 rounded hover:bg-red-700 disabled:opacity-50"
                >
                  {deleting ? "Deleting..." : "Confirm"}
                </button>
                <button
                  onClick={() => setConfirmDelete(false)}
                  className="px-4 py-1 bg-gray-300 rounded hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            )}

            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        )
      }
    >
      {category ? (
        <>
          <div>
            <label className="block text-sm font-medium">Code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              className="w-full px-2 py-1 border rounded"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium">Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-2 py-1 border rounded"
            />
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium">Sub Categories</label>
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newSubCategory}
                onChange={(e) => setNewSubCategory(e.target.value)}
                placeholder="Enter sub category"
                className="flex-1 px-2 py-1 border rounded"
              />
              <Button type="button" onClick={handleAddSubCategory}>
                Add
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {subCategories.map((sub, idx) => (
                <div
                  key={idx}
                  className="flex items-center gap-1 bg-gray-200 px-2 py-1 rounded-full text-sm"
                >
                  <span>{sub}</span>
                  <button
                    type="button"
                    onClick={() => handleRemoveSubCategory(sub)}
                    className="text-gray-600 hover:text-red-600"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      ) : (
        <p>No category selected.</p>
      )}
    </Dialog>
  );
};

export default CategoryDetailsDialog;
